'use client';

import { CustomEventName, GTMConstants, GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';
import { NewsTagGtmEventType } from '@fe-common-ui/Button';
import { QuoteGtmEventType, TagsGroup } from '@fe-common-ui/Group';
import List, { CategoryNewsListGtmEventType } from '@fe-common-ui/List';
import Section from '@fe-common-ui/Section';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { useIsInteractingObserver } from '@fe-common-utils/libs/hooks/useIsInteractingObserver';
import { QUOTES_CODE_MAPPING } from '@fe-common-utils/libs/quote';
import DefaultSkeleton from '@fe-news/components/Skeletions/InterestedSkeletion';
import DesktopAdProfiles from '@fe-news/constants/DFP/DesktopAdProfiles';
import { styled } from '@linaria/react';
import { useQuery } from '@tanstack/react-query';
import { Fragment, useCallback, useRef } from 'react';
import queries from '@fe-news/components/ReactQuery';
import DynamicAd from '../DynamicAd/DynamicAd';

const NEWS_LIMIT = 8;

const GTM_EVENT_IMP_PLACEMENT = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.IMP_PLACEMENT,
  [GTMEventKey.SECTION]: `${GTMConstants.SECTION_PLACEHOLDER}_相關新聞`
};
const OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0.1
};

enum AdPos {
  FIRST = 3,
  SECOND = 7
}
const ADS = {
  [AdPos.FIRST]: DesktopAdProfiles.NewsDetailInterested1,
  [AdPos.SECOND]: DesktopAdProfiles.NewsDetailInterested2
};

type RelatedProps = {
  id: number;
};

const Container = styled.section`
  margin-bottom: 40px;
  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    display: none;
  }
`;

const ListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

type QuotesType = Record<string, { name: string; ratio: number; marketLink: string; symbol: string }>;

const formatQuotes = (quotes: Quote[]) => {
  const result: QuotesType = {};

  if (!quotes) return result;

  for (const q of quotes) {
    const symbol = q[QUOTES_CODE_MAPPING.Symbol as QuoteKeys] as string;
    const name = q[QUOTES_CODE_MAPPING.ChineseName as QuoteKeys] as string;
    const ratio = q[QUOTES_CODE_MAPPING.ChangePercent as QuoteKeys] as number;
    const marketLink = q[QUOTES_CODE_MAPPING.Url as QuoteKeys] as string;

    result[symbol] = { name, ratio, marketLink, symbol };
  }

  return result;
};

const NewsListItem = ({
  title,
  newsId,
  publishAt,
  coverSrc,
  categoryName,
  market,
  summary,
  columnists,
  keyword,
  quotes,
  isLast
}: {
  newsId: number;
  title: string;
  publishAt: number;
  coverSrc: Record<'s' | 'm' | 'l', { src: string; width: number; height: number }>;
  categoryName: string;
  market: { code: string; name: string; symbol: string }[];
  summary?: string;
  columnists: ColumnistsType | null;
  keyword?: string[];
  quotes?: QuotesType;
  isLast?: boolean;
}) => {
  if (!quotes) {
    return (
      <List.CategoryNewsList
        title={title}
        newsId={newsId}
        publishAt={publishAt}
        coverSrc={coverSrc?.s?.src}
        breadcrumbs={categoryName}
        backgroundColor='inherit'
        Tags={<TagsGroup tags={keyword} gtmEventNewsTagType={NewsTagGtmEventType.NEWS_ARTICLE_INTERESTED} />}
        isLast={isLast}
        gtmEventType={CategoryNewsListGtmEventType.NEWS_CATEGORY_INTERESTED}
        isCelebrityArea={false}
        summary={summary}
        columnists={columnists}
      />
    );
  }

  const otherProduct = market?.map(({ symbol }) => quotes[symbol] || {}) || [];

  return (
    <List.CategoryNewsList
      title={title}
      newsId={newsId}
      publishAt={publishAt}
      coverSrc={coverSrc?.s?.src}
      breadcrumbs={categoryName}
      backgroundColor='inherit'
      Tags={
        <TagsGroup
          tags={keyword}
          otherProduct={otherProduct}
          gtmEventNewsTagType={NewsTagGtmEventType.NEWS_ARTICLE_INTERESTED}
          gtmEventQuoteType={QuoteGtmEventType.NEWS_ARTICLE_INTERESTED}
        />
      }
      isLast={isLast}
      gtmEventType={CategoryNewsListGtmEventType.NEWS_CATEGORY_INTERESTED}
      isCelebrityArea={false}
      summary={summary}
      columnists={columnists}
    />
  );
};

const RelatedList = ({ data, quotes }: { data: News[]; quotes?: QuotesType }) => {
  if (!data || data.length === 0) return <DefaultSkeleton limit={10} />;

  return data.map((item: News, index: number) => (
    <Fragment key={item.newsId}>
      <NewsListItem {...item} quotes={quotes} />
      {index in AdPos && (
        <DynamicAd key={`related-sponsor-ad-${index}`} desktopAdProfile={ADS[index as AdPos]} mobileAdProfile={null} />
      )}
    </Fragment>
  ));
};

export default function Related({ id }: RelatedProps) {
  const { data } = useQuery({
    ...queries.news.newsRelated({ id, limit: NEWS_LIMIT }),
    initialData: [],
    notifyOnChangeProps: ['data']
  });

  const symbols = data?.reduce((array: string[], { market }: Pick<News, 'market'>) => {
    const newsSymbols = market?.map(({ symbol }) => symbol) || [];
    array.push(...newsSymbols);
    return array;
  }, []) || [''];

  const { data: quotes } = useQuery({
    ...queries.quotes.quotesBySymbolsKeys(symbols),
    select: formatQuotes,
    enabled: symbols.length > 0,
    notifyOnChangeProps: ['data']
  });

  const countFirstEnter = useRef(0);

  const onEnter = useCallback(() => {
    if (!countFirstEnter.current) {
      countFirstEnter.current = 1;
      sendGTM(GTM_EVENT_IMP_PLACEMENT);
    }
  }, []);

  const reference = useIsInteractingObserver(onEnter, OBSERVER_OPTIONS);

  return (
    <Container ref={reference}>
      <Section title='相關新聞' />
      <ListContainer>
        <RelatedList data={data} quotes={quotes} />
      </ListContainer>
    </Container>
  );
}
